import React, { useState, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { apiService } from '../services/api';

export const GeoFilter = ({ selectedGeo, onGeoChange, geosWithData = [], geosWithSpikes = [] }) => {
  const [activeGeos, setActiveGeos] = useState([]);
  const [allGeosCatalog, setAllGeosCatalog] = useState([]);

  useEffect(() => {
    const loadGeos = async () => {
      try {
        const res = await apiService.getSettings();
        if (res.data && res.data.active_geos) {
          setActiveGeos(res.data.active_geos);
        }
        if (res.data && Array.isArray(res.data.all_geos)) {
          setAllGeosCatalog(res.data.all_geos);
        }
      } catch (err) {
        console.error('Failed to load GEOs:', err);
        setActiveGeos(['BR','DE','FR','IT','ES','PL','ID','TH','VN','IN']);
        setAllGeosCatalog([]);
      }
    };
    loadGeos();
  }, []);

  const geoByCode = {};
  allGeosCatalog.forEach(g => {
    if (g?.code) geoByCode[String(g.code).toUpperCase()] = g;
  });

  const grouped = {};
  activeGeos.forEach((codeRaw) => {
    const code = String(codeRaw || '').toUpperCase();
    if (!code) return;
    const meta = geoByCode[code] || { code, name: code, region: 'Other' };
    const region = String(meta.region || 'Other').toUpperCase();
    if (!grouped[region]) grouped[region] = [];
    grouped[region].push({ code, name: meta.name || code });
  });
  Object.keys(grouped).forEach(region => {
    grouped[region].sort((a, b) => a.code.localeCompare(b.code));
  });

  // Состояние страны определяется в одном месте. Раньше цвет задавали две
  // независимые функции: одна красила в зелёный по «есть данные», другая в
  // голубой — в итоге зелёным светились все страны подряд, и отличить страну
  // со всплесками от страны с любыми данными было нельзя.
  const geoState = (code) => {
    if (selectedGeo === code) return 'selected';
    if (geosWithSpikes.includes(code)) return 'spikes';
    if (geosWithData.includes(code)) return 'data';
    return 'none';
  };

  // Цвет ведёт className, поэтому вариант везде нейтральный, кроме выбранной.
  const getBadgeStyle = (code) => (geoState(code) === 'selected' ? 'default' : 'outline');

  const GEO_STATE_CLASS = {
    selected: 'cursor-pointer',
    spikes: 'cursor-pointer bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100',
    data: 'cursor-pointer bg-sky-50 text-sky-700 border-sky-300 hover:bg-sky-100',
    none: 'cursor-pointer text-muted-foreground border-border opacity-60 hover:opacity-100',
  };

  const getBadgeClass = (code) => GEO_STATE_CLASS[geoState(code)];

  return (
    <Card className="card-elevated">
      <CardHeader>
        <CardTitle className="text-lg font-display flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary" />
          Filter by GEO
        </CardTitle>
        <div className="flex gap-3 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> spikes</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-500"></span> data</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zinc-600"></span> no data</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">ALL</p>
            <Badge
              variant={selectedGeo === null ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => onGeoChange(null)}
            >
              All Regions
            </Badge>
          </div>
          {Object.entries(grouped).map(([region, geos]) => {
            if (!geos.length) return null;
            const codes = geos.map(g => g.code);
            const withData = codes.filter(c => geosWithData.includes(c)).length;
            const withSpikes = codes.filter(c => geosWithSpikes.includes(c)).length;
            return (
              <div key={region}>
                <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                  {region} {withSpikes > 0 && <span className="text-emerald-700">({withSpikes} with spikes)</span>}{withSpikes === 0 && withData > 0 && <span className="text-sky-400">({withData} with data)</span>}
                </p>
                <div className="flex flex-wrap gap-2">
                  {geos.map(({ code, name }) => (
                    <Badge
                      key={code}
                      variant={selectedGeo === code ? 'default' : 'outline'}
                      className={getBadgeClass(code)}
                      onClick={() => onGeoChange(code)}
                    >
                      {code} - {name || code}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
